import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const openChat = (mode) => window.dispatchEvent(new CustomEvent("sp:open-chat", { detail: { mode } }));

const CalcLink = () => <a href="#calculator" className="faq-link">Battery Savings Calculator</a>;
const AiLink = ({ mode = "qa", children }) => (
  <button type="button" onClick={() => openChat(mode)} className="faq-link">{children}</button>
);

const GROUPS = [
  {
    title: "Pre-order & fulfilment",
    items: [
      {
        q: "When will my iron ship?",
        a: <>Our next production batch lands in <strong>November</strong>. Pre-orders are reserved in the order they arrive and dispatched as soon as the batch clears our final quality checks — we message every customer their exact dispatch date. After dispatch, Lagos deliveries typically arrive in <strong>2–4 business days</strong> and other states in <strong>3–7 business days</strong>. Full timelines are on the <Link to="/delivery-policy" className="faq-link">Delivery page</Link>.</>,
      },
      {
        q: "Will this work on my 1kVA / 1.5kVA inverter?",
        a: <>Yes — the CORE 500 was specifically designed <strong>not</strong> to overload small inverters. It draws about 500W (roughly a quarter of an ordinary 1,500–2,400W iron) with a low-surge startup, so a 1kVA or 1.5kVA inverter runs it comfortably alongside your lights and fan without tripping. Unsure about your setup? <AiLink mode="sales">Ask Flo</AiLink> for a quick compatibility check.</>,
      },
      {
        q: "What payment methods are accepted?",
        a: <>We accept <strong>Mastercard, Visa, Verve and Bank Transfer</strong>, all processed securely through Paystack at checkout. Prefer to order with a person? Message us on <a href="https://wa.me/2347072260372" className="faq-link">WhatsApp</a> and we'll send a secure payment link.</>,
      },
    ],
  },
  {
    title: "Power & compatibility",
    items: [
      {
        q: "Will the CORE 500 work with my inverter?",
        a: <>Yes — that's exactly what it was built for. At 500W it draws roughly a quarter of what an ordinary 1,500–2,400W iron pulls, so a 1kVA inverter or larger runs it comfortably without tripping or dimming your lights.</>,
      },
      {
        q: "What size inverter and battery do I need?",
        a: <>A 1kVA inverter handles the CORE 500 comfortably, and even modest battery banks benefit from the lighter load. The bigger your setup, the longer everything else runs alongside it. Use the <CalcLink /> above to see the impact on your exact setup, or <AiLink mode="sales">ask our AI assistant</AiLink> for a personalised recommendation.</>,
      },
      {
        q: "Can I use it with a solar power system?",
        a: <>Absolutely. The CORE 500 was engineered from day one for solar-powered homes — its low draw means your panels and batteries keep up even on regular days.</>,
      },
      {
        q: "Can I use it with a generator?",
        a: <>Yes. Small household generators handle the CORE 500 easily, and because the load is lower, you burn noticeably less fuel per ironing session.</>,
      },
      {
        q: "Does it work during power outages?",
        a: <>It runs on whatever backup you have — solar, inverter or generator. That low 500W draw is the whole point: ironing stays possible when the grid is off.</>,
      },
      {
        q: "How much battery power does it save compared to a regular iron?",
        a: <>Compared to a typical 2,000W iron, the CORE 500 cuts the load by around 75% — that's power your battery keeps for lights, fans and everything else. Get your personal number with the <CalcLink />.</>,
      },
    ],
  },
  {
    title: "Ironing performance",
    items: [
      {
        q: "Will a 500W iron actually press as well as a 2,000W iron?",
        a: <>Yes — for everyday ironing, the results speak for themselves. The CORE 500 is engineered for efficient pressing: a thermostat-controlled soleplate holds steady, even heat instead of wasting watts. High wattage mostly buys faster reheating, not a crisper shirt. What you give up in raw power, you gain in an iron that actually works on your inverter, solar or generator.</>,
      },
      {
        q: "Will it iron thick fabrics like Ankara, denim, linen, lace and cotton?",
        a: <>Yes. The adjustable thermostat lets you match the heat to the fabric — higher settings for Ankara, denim and heavy cotton; lower for lace and delicates. For stubborn creases, a light misting of the fabric before pressing works wonders.</>,
      },
      {
        q: "Does it heat up as quickly as a conventional iron?",
        a: <>It heats up quickly and holds a steady temperature through the session. A 2,400W iron reheats slightly faster between garments — but it also can't run on your inverter. For the morning rush on backup power, the CORE 500 is built to keep pace.</>,
      },
      {
        q: "Is the soleplate non-stick and safe for delicate fabrics?",
        a: <>Yes — the ceramic non-stick soleplate glides smoothly without sticking or leaving shine marks. For delicate fabrics like lace and silk-blends, simply use a lower thermostat setting.</>,
      },
    ],
  },
  {
    title: "Warranty, delivery & returns",
    items: [
      {
        q: "How long is the warranty and what does it cover?",
        a: <>Every CORE 500 carries a <strong>12-month limited warranty</strong> covering verified manufacturing defects and electrical faults from normal household use. Full coverage details, exclusions and the claim process are on our dedicated <Link to="/warranty" className="faq-link">Warranty page</Link>.</>,
      },
      {
        q: "How do I make a warranty claim?",
        a: <>Message us on <a href="https://wa.me/2347072260372" className="faq-link">WhatsApp</a> or email info@sunpresssolar.com with your order reference, the product serial number and photos of the fault. We respond in 1–2 business days and resolve verified claims by repair or replacement after inspection — the step-by-step process is on the <Link to="/warranty" className="faq-link">Warranty page</Link>.</>,
      },
      {
        q: "How long does delivery take within Nigeria?",
        a: <>Lagos orders typically arrive in 2–4 business days; other states 3–7. Shipping is calculated at checkout — Lagos Island ₦3,500, Lagos Mainland ₦5,000, other states ₦7,500 — and always shown before you pay. Full details on the <Link to="/delivery-policy" className="faq-link">Delivery page</Link>.</>,
      },
      {
        q: "Do you ship across Africa?",
        a: <>We currently deliver Nigeria-wide, with West African expansion planned. For bulk or international orders, <Link to="/contact" className="faq-link">contact us</Link> — we'll find a way.</>,
      },
      {
        q: "Can I return it if I change my mind?",
        a: <>Yes — 7-day returns on unused items in their original packaging. See the <Link to="/refund-policy" className="faq-link">Refund policy</Link> for the simple steps.</>,
      },
    ],
  },
  {
    title: "Payments, orders & more",
    items: [
      {
        q: "What payment methods do you accept?",
        a: <>Secure card payment via Paystack (Visa, Mastercard, Verve and bank transfer) — or order directly on <a href="https://wa.me/2347072260372" className="faq-link">WhatsApp</a> if you prefer a human touch.</>,
      },
      {
        q: "Can I pay on delivery?",
        a: <>Not at the moment — all orders are securely prepaid via Paystack or arranged on WhatsApp. This keeps prices lower and delivery faster for everyone.</>,
      },
      {
        q: "Can I become a distributor or reseller?",
        a: <>Yes — we're actively onboarding distributors across Nigeria and West Africa, with wholesale pricing and marketing support. <Link to="/distributors" className="faq-link">Apply on the Distributor page</Link>.</>,
      },
      {
        q: "Will accessories like MistPro, SteamPad and the Tailor Edition Kit be available?",
        a: <>The <Link to="/products/MIST-500" className="faq-link">MistPro Spray Bottle – 500ml</Link> is available now for ₦4,200. The SteamPad and Tailor Edition Kit are in development and will be announced here first. Want a heads-up? <a href="https://wa.me/2347072260372?text=Notify%20me%20when%20SunPress%20accessories%20launch" className="faq-link">Join the WhatsApp list</a>.</>,
      },
      {
        q: "Why choose the CORE 500 instead of a conventional 1,500–2,400W iron?",
        a: <>Because a conventional iron fights your power setup — spiking the load, tripping inverters and draining batteries. The CORE 500 does the same daily job on roughly a quarter of the power, pays for itself in saved energy, and carries a <Link to="/warranty" className="faq-link">12-month limited warranty</Link>. See the full comparison table above, or <AiLink mode="qa">ask our AI assistant</AiLink> anything.</>,
      },
    ],
  },
];

export default function FAQSection() {
  return (
    <section data-testid="faq-section" className="py-20 md:py-28" style={{ background: "var(--surface)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow">Questions, answered</div>
            <h2 className="display mt-4" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", color: "var(--ink)" }}>
              Everything people ask <span className="sunset-text">before they buy</span>.
            </h2>
          </div>
          <div className="md:col-span-5" style={{ color: "var(--ink-soft)" }}>
            Can't find your question?{" "}
            <button type="button" onClick={() => openChat("qa")} data-testid="faq-ask-ai" className="faq-link inline-flex items-center gap-1">
              <MessageCircle size={14} /> Ask our AI assistant
            </button>{" "}
            — it knows the CORE 500 inside out.
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-x-12 gap-y-10">
          {GROUPS.map((g, gi) => (
            <div key={g.title}>
              <h3 className="text-xs uppercase tracking-[0.2em] font-mono font-medium" style={{ color: "var(--ink-soft)" }}>{g.title}</h3>
              <Accordion type="single" collapsible className="mt-4">
                {g.items.map((item, i) => (
                  <AccordionItem key={item.q} value={`faq-${gi}-${i}`} style={{ borderColor: "var(--border)" }}>
                    <AccordionTrigger
                      data-testid={`faq-trigger-${gi}-${i}`}
                      className="text-left text-[0.95rem] font-medium hover:no-underline py-4"
                      style={{ color: "var(--ink)" }}
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
